import { GenericObject, IClaims, KeyTitleObject } from "src/defs";
import { CanvasConfig } from "./user.service.interface";
import { ReportConfig } from "src/components/reporting/types";
import { ReviewerConfig } from "src/components/new-design/canvas/reviewer-actions/types";
import { AnnotationDisplayRule } from "src/components/new-design/canvas/types";
export interface PageConfig {
  size: number;
  offset: number;
}

export interface SortConfig {
  direction: string;
  property: string;
}

export interface PaginationConfig {
  page?: PageConfig;
  sort?: Array<SortConfig>;
}

export interface AnalyticsCard extends GenericObject {
  index: number;
  title: string;
  caption?: string;
  /** All Claims tile key (Accept/Pend/…). Prefer over filterKey when both set. */
  overjetReviewResult?: string;
  /** Queue tile key (Total / needs_human_rs_review). Used when overjetReviewResult is absent. */
  filterKey?: string;
  color?: string;
}

export interface HeaderConfig extends GenericObject {
  colName: string;
  displayName: string;
  checked: boolean;
}

export interface DashboardConfig {
  claimsTable?: ClaimsTable;
  claimTableTabs?: Array<any>;
  tableTabs?: Array<any>;
  analyticsCard?: Array<AnalyticsCard>;
  showSystemFilters?: boolean;
}

export interface ClaimsTable {
  fmxWorkflowEnabled?: boolean;
  orthoDisplayEnabled?: boolean;
  claimLevel: boolean;
  columnsToHide: Array<string>;
}

export interface DateRangeFilterFieldOption {
  value: string;
  label: string;
}

/** Pages where a config-driven filter may render. */
export type FilterPage = "home" | "queue";

/** Metadata-driven page filter; parent map key is the filter id. Options are always static (values). */
export interface ClientFilterConfig {
  /** Display order (ascending). */
  order: number;
  /** Filter label / placeholder for the multi-select. */
  title: string;
  type: string;
  values?: Array<KeyTitleObject>;
  /** Top-level GraphQL variable name (e.g. ingressFilter, lobTypeFilter). */
  requestField?: string;
  /** When set, enables popover mode and sets the popover header (e.g. long state lists). */
  popoverTitle?: string;
  /** Must be true for the filter to render; omitted or false hides it. */
  enabled?: boolean;
  /** Pages where this filter renders. Valid values: "home", "queue". */
  pages?: FilterPage[];
}

export interface QueueTableTab {
  index: number;
  title: string;
  /**
   * Queue tab filter key: empty = All; e.g. needs_human_rs_review = Escalated.
   * (All Claims tabs use overjetReviewResult; FE getTabs accepts either.)
   */
  filterKey?: string;
  /** Optional accent color (e.g. Deny red for Escalated). */
  color?: string;
  /** @deprecated Queue config should use filterKey. Kept for older YAML / dual-read. */
  overjetReviewResult?: string;
  /** When true, this tab is selected on first load (Queue only; All Claims uses saved filter). */
  default?: boolean;
}

export interface QueueConfig {
  analyticsCard: Array<AnalyticsCard>;
  queueHeaders: Array<string>;
  queueHeadersConfig: Array<HeaderConfig>;
  showSpecialtyFilter?: string;
  showDateRangeFilter?: boolean;
  /**
   * When non-empty, Queue shows All | Escalated tabs (identity via filterKey).
   * Omit for legacy Queue with no tabs.
   */
  tableTabs?: Array<QueueTableTab>;
}

export interface FraudWasteAbuse extends GenericObject {
  summaryHeaders: Array<any>;
  dashboardConfig: GenericObject;
  clientHeadersConfig: GenericObject;
}

export type PatientHistoryColumnFormat =
  | "string"
  | "number"
  | "date"
  | "firstName"
  | "lastName";

export interface PatientHistoryColumn {
  title: string;
  dataIndex: string;
  key: string;
  width?: number;
  format?: PatientHistoryColumnFormat;
  searchable?: boolean;
}

export interface PatientHistoryConfig {
  expandedByDefault?: boolean;
  claimColumns?: Array<PatientHistoryColumn>;
  procedureColumns?: Array<PatientHistoryColumn>;
}

interface crowdingDisplayConfig {
  value: Array<number>;
  display: string;
}

export interface ClientConfig extends GenericObject {
  activeProcedures: Array<string>;
  dateRangeFilterFields?: Array<DateRangeFilterFieldOption>;
  /** Config-driven filters for home and/or queue pages. Keys are filter ids; use `pages` on each filter. */
  queueFilters?: Record<string, ClientFilterConfig>;
  canvasConfig?: CanvasConfig;
  claimCheckoutControlEnabled?: boolean;
  claimLevelCanvasConfig?: CanvasConfig;
  claimSummaryHeaders?: Array<string>;
  claims?: GenericObject;
  headersConfig?: Array<IClaims.IVisibleColsDictionary> | Array<GenericObject>;
  clientHeaders?: Array<string>;
  predictionConfig?: GenericObject;
  dashboardConfig?: DashboardConfig;
  queueConfig?: QueueConfig;
  claimLevelQueueConfig?: QueueConfig;
  fraudWasteAbuse: FraudWasteAbuse;
  tabConfig?: GenericObject;
  graphqlQueries?: GenericObject;
  claimLevelGraphqlQueries?: GenericObject;
  reviewPassGraphqlQueries?: GenericObject;
  claimLevelDashboardConfig?: DashboardConfig;
  userDefaultRoute?: string;
  clientDefaultRoute?: string;
  defaultRoute?: string;
  reportsConfig: Array<ReportConfig>;
  clientName: string;
  inActivityLogoutPeriod?: string | number;
  showAttestationBox?: boolean;
  showPatientHistory?: boolean;
  patientHistoryConfig?: PatientHistoryConfig;
  enableAccessibility?: boolean;
  reviewConfig?: Array<ReviewerConfig>;
  annotationDisplayRules?: Array<AnnotationDisplayRule>;
  colorDecisionField?: string;
  claimDeliveryType?: string;
  restrictProcedureLevelView?: boolean;
  crowdingDisplayConfig?: Array<crowdingDisplayConfig>;
  toothNumberingSystem?: string;
  enableMultiServiceLineSelect?: boolean;
  clientDateDisplayTimezone?: string;
}

export interface SAMLattributes {
  client_id: string;
  first_name: string;
  last_name: string;
  nickname: string;
  role: string;
  role_id: string;
}

export interface Authority {
  authority: string;
}

export interface UserInformation {
  stateLicenses?: GenericObject;
  firstName?: string;
  lastName?: string;
  emailId?: string;
  licenseSpeciality?: string;
}

export interface UserProfile {
  accountNonExpired?: boolean;
  accountNonLocked?: boolean;
  authorities: Array<Authority>;
  clientConfig: ClientConfig;
  permissionObject: GenericObject;
  email?: string;
  username?: string;
  roles?: Array<string>;
  supportedOrthoCodes?: Array<string>;
  userInformation?: UserInformation;
  phiRestricted?: boolean;
}

export interface PredictionConfigObject {
  anteriorRCTnoCrown?: string;
  autoAcceptPosteriorRCT?: string;
  autoApprove?: boolean;
  autoPend?: boolean;
  boneLevel?: number;
  boneLoss?: number;
  calculationRate?: number;
  calculusPresent?: boolean;
  decayOrFracturePresentForDMF?: boolean;
  decayPresentForDMF?: boolean;
  defectiveCrownPresent?: boolean;
  downgradeAllProcToOne?: boolean;
  downgradeCode?: string;
  downgradePlans?: Array<any>;
  existCrownNoRCTnoDecay?: string;
  existCrownWithRCTNoDecay?: string;
  fullDentureApproved?: boolean;
  furcationPresent?: boolean;
  juvenileAggressivePerio?: string;
  maxBoneLevel?: number;
  maxBoneRatio?: string;
  maxCrownImpacted?: string;
  maxDmfRatio?: number;
  minAge?: number;
  minBoneRatio?: string;
  minCrownImpacted?: string;
  minDmfRatio?: number;
  numberOfTeethPerQuadrant?: number;
  paidHistoryPresent?: boolean;
  posteriorExistCrownWithDecay?: string;
  rctObturation?: number;
  rctPresentOnPosteriorTooth?: boolean;
  reviewIfPAWithOcclusalOnly?: boolean;
  imageTypeConfig: GenericObject;
  ruleExecutionIdConfig?: GenericObject;
  ruleExecutionIdStartsWithAnyOf?: GenericObject;
}

export interface PredictionConfigI {
  [procedure: string]: PredictionConfigObject;
}

export enum CanvasTypes {
  DEFAULT_CANVAS = "canvas",
  ORTHO_CANVAS = "ortho",
}

export interface ClaimDetails {
  canvasType: CanvasTypes;
}

export interface CachedClaimRecord {
  [key: string]: ClaimDetails;
}

export interface ClaimIDsCache {
  ids: Array<string>;
  cachedClaimRecords: CachedClaimRecord;
  pageOffset: number;
  size: number;
  totalElements: number;
}
